import React, { FC } from 'react';
import Image from 'next/image';
import { Article } from 't2-graphql-types';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash/isEqual';
import { useRouter } from 'next/compat/router';
import { formatDate } from '../../../utils/formatDate';
import { useModal } from '../Modal/useModal';
import { ShareArticleModalContainer } from '../ShareModal/ShareArticleModalContainer';
import IconButton from '../IconButton/IconButton';
import ShareIcon from '../Icons/ShareIcon';
import { TCloudinaryImageShape } from '../CloudinaryResponsiveImage/types';
import PromptDetailsItem from './PromptDetailsItem';
import ThreeDotsIcon from '../Icons/ThreeDotsIcon';
import Dropdown from '../Dropdown/Dropdown';
import { PromptHeaderDropdownMenu } from './PromptHeaderDropdownMenu';
import { getFeatureFlag } from '../FeatureFlags/getFeatureFlag';
import { EditPromptModal } from '../EditPrompt/EditPromptModal';
import { DeletePromptConfirmationModal } from '../DeletePrompt/DeletePromptConfirmationModal';
import { CloseConfirmationModal } from '../ConfirmationModal/CloseConfirmationModal';
import Label from '../Label/Label';
import { AnalyticsModalType } from '../Modal/constants';
import { PromptAuthorLink } from './PromptAuthorLink';
import { usePromptHeaderAnalytics } from './hooks/usePromptHeaderAnalytics';
import { PromptDetailsLiveCard } from './PromptDetailsLiveCard';
import { TPromptSettingsProps } from '../PromptCard/types';
import { T2ProfileCardContainer } from '../ProfileCard/T2ProfileCardContainer';
import { UnstyledTooltip } from '../Tooltip/UnstyledTooltip';
import { PROFILE_CARD_HOVER_DELAY } from '../ProfileCard/constants';
import { PromptReplyEligibleRolesStackContainer } from '../PromptReplyEligibleRolesStack/PromptReplyEligibleRolesStackContainer';
import { useReactiveAuthenticatedUser } from '../Auth/hooks/useAuthenticatedUser';
import { getPromptEditPageUrl } from '../../services/pagesUrl/getPromptEditPageUrl';

type TPromptDetailsProps = {
  authorId: string;
  name: string;
  username: string;
  profileImage: TCloudinaryImageShape | string;
  createdAt: string;
  url: string;
  canEditPrompt: boolean;
  canDeletePrompt: boolean;
  territorySlug: string;
  promptData: Article;
  territoryId: string;
  coverImage?: string;
};

const PromptDetails: FC<TPromptDetailsProps> = ({
  authorId,
  name,
  username,
  profileImage,
  createdAt,
  canEditPrompt,
  canDeletePrompt,
  territorySlug,
  promptData,
  territoryId,
  coverImage,
}) => {
  const { authenticatedUserId } = useReactiveAuthenticatedUser();
  const analytics = usePromptHeaderAnalytics();
  const { openModal: openShareModal, ModalComponent: ShareModalComponent } = useModal({
    analytics: { modalName: AnalyticsModalType.promptShare },
  });
  const router = useRouter();

  const {
    openModal: openPromptConfirmationModal,
    closeModal: closePromptConfirmationModal,
    ModalComponent: PromptConfirmationModalComponent,
  } = useModal();

  const showPromptHeaderDropdown = (canEditPrompt || canDeletePrompt) && getFeatureFlag('promptHeaderDropdown');
  const showLivePrompts = getFeatureFlag('livePrompts');
  const { title, shortDescription, promptSettings } = promptData;

  const defaultValues = {
    title: title as string,
    description: shortDescription as string,
    startDate: new Date(promptSettings?.startDate),
    endDate: new Date(promptSettings?.endDate),
  };

  const useFormReturn = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const onCloseEditPromptModal = () => {
    const {
      formState: { isSubmitting, defaultValues },
      getValues,
    } = useFormReturn;

    if (!isSubmitting && !isEqual(getValues(), defaultValues)) {
      openPromptConfirmationModal();
    }
  };
  const {
    openModal: openEditPromptModal,
    closeModal: closeEditPromptModal,
    ModalComponent: EditPromptModalComponent,
  } = useModal({ closeModalCallback: onCloseEditPromptModal, analytics: { modalName: AnalyticsModalType.promptEdit } });

  const {
    openModal: openDeletePromptModal,
    closeModal: closeDeletePromptModal,
    ModalComponent: DeletePromptModalComponent,
  } = useModal({ analytics: { modalName: AnalyticsModalType.promptDelete } });

  const onShareButtonClick = () => {
    analytics.sendShareButtonClickEvent();
    openShareModal();
  };

  const handleEditPrompt = () => {
    if (getFeatureFlag('promptV2')) {
      router?.push(getPromptEditPageUrl(promptData?.slug));
    } else {
      openEditPromptModal();
    }
  };

  return (
    <>
      <ShareModalComponent>
        <ShareArticleModalContainer articleType="PROMPT" />
      </ShareModalComponent>
      <div className="flex mt-20 lg:mt-32 flex-col gap-y-4 col-span-full lg:col-start-9 lg:col-span-4  order-1 lg:order-2">
        {coverImage && (
          <Image src={coverImage} width={450} height={230} alt="cover" className="h-[230px] object-cover rounded-lg" />
        )}
        <PromptDetailsItem>
          <div className="min-w-[120px]">
            <Label textColor="secondary" size="medium">
              Created by
            </Label>
          </div>
          <UnstyledTooltip
            floatingUIHover={PROFILE_CARD_HOVER_DELAY}
            trigger={
              <PromptAuthorLink author={{ name, username, profileImage: profileImage as TCloudinaryImageShape }} />
            }
          >
            <T2ProfileCardContainer id={authorId} />
          </UnstyledTooltip>
        </PromptDetailsItem>

        <PromptDetailsItem>
          <div className="min-w-[120px]">
            <Label textColor="secondary" size="medium">
              Created on
            </Label>
          </div>
          {formatDate(createdAt)}
        </PromptDetailsItem>

        {authenticatedUserId && (
          <PromptDetailsItem>
            <div className="min-w-[120px]">
              <Label textColor="secondary" size="medium">
                Eligible roles
              </Label>
            </div>
            <PromptReplyEligibleRolesStackContainer territoryId={territoryId} territorySlug={territorySlug} />
          </PromptDetailsItem>
        )}

        <PromptDetailsItem>
          <div className="flex gap-x-4">
            <IconButton onClick={onShareButtonClick}>
              <ShareIcon className="w-4 h-4 md:w-5 md:h-5" />
            </IconButton>
            {showPromptHeaderDropdown && (
              <Dropdown
                onStateChange={(state) => {
                  if (state.isOpen) {
                    analytics.sendPromptSettingsDropdownOpenEvent();
                  } else {
                    analytics.sendPromptSettingsDropdownCloseEvent();
                  }
                }}
                label={
                  <IconButton tag="div">
                    <ThreeDotsIcon />
                  </IconButton>
                }
                menu={
                  <PromptHeaderDropdownMenu
                    canEdit={canEditPrompt}
                    canDelete={canDeletePrompt}
                    handleEdit={handleEditPrompt}
                    handleDelete={openDeletePromptModal}
                  />
                }
              />
            )}
          </div>
        </PromptDetailsItem>
        {showLivePrompts && promptData?.promptSettings?.startDate && (
          <PromptDetailsLiveCard promptSettings={promptData?.promptSettings as TPromptSettingsProps} />
        )}
      </div>
      <EditPromptModal
        ModalComponent={EditPromptModalComponent}
        closeModal={() => {
          useFormReturn?.reset(useFormReturn?.getValues());
          closeEditPromptModal();
        }}
        useFormReturn={useFormReturn}
        promptData={promptData}
      />
      <DeletePromptConfirmationModal
        ModalComponent={DeletePromptModalComponent}
        closeModal={closeDeletePromptModal}
        promptId={promptData?.id}
        territorySlug={territorySlug}
        territoryId={territoryId}
      />
      <CloseConfirmationModal
        ModalComponent={PromptConfirmationModalComponent}
        closeConfirmationModal={closePromptConfirmationModal}
        closeModal={closeEditPromptModal}
        openModal={openEditPromptModal}
      />
    </>
  );
};

export default PromptDetails;
